import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  notificationApi,
  type NotificationTemplatePreview as Preview,
  type NotificationTemplateSummary
} from '../../services/api';

type Channel = 'html' | 'text';
type Width = 'mobile' | 'desktop';

/**
 * Admin → Notifieringar → Förhandsvisning (ClickUp 869fkva4j): every e-mail
 * template rendered with its fixture, as HTML or text, at phone or desktop
 * width, without triggering a real event. The HTML runs in a sandbox without
 * scripts.
 */
export function NotificationTemplatePreview() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<NotificationTemplateSummary[] | null>(null);
  const [eventKey, setEventKey] = useState('');
  const [language, setLanguage] = useState<'sv' | 'en'>('sv');
  const [channel, setChannel] = useState<Channel>('html');
  const [width, setWidth] = useState<Width>('desktop');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    notificationApi.getTemplates()
      .then(list => {
        setTemplates(list);
        setEventKey(list[0]?.eventKey ?? '');
      })
      .catch(() => {
        setTemplates([]);
        setError(true);
      });
  }, []);

  useEffect(() => {
    if (!eventKey) return;
    let current = true;
    setPreview(null);
    setError(false);
    notificationApi.previewTemplate(eventKey, language)
      .then(result => { if (current) setPreview(result); })
      .catch(() => { if (current) setError(true); });
    return () => { current = false; };
  }, [eventKey, language]);

  // The e-mail decides its own height, also when the stage gets narrower:
  // the frame follows the height of its body.
  const observer = useRef<ResizeObserver | null>(null);
  useEffect(() => () => observer.current?.disconnect(), []);
  const fitFrame = () => {
    const body = frame.current?.contentDocument?.body;
    observer.current?.disconnect();
    if (!frame.current || !body) return;
    const fit = () => {
      if (frame.current) frame.current.style.height = `${Math.ceil(body.getBoundingClientRect().height)}px`;
    };
    fit();
    // The frame's own observer: it sees layout changes in its document.
    const FrameObserver = (frame.current.contentWindow as (Window & typeof globalThis) | null)?.ResizeObserver;
    if (FrameObserver) {
      observer.current = new FrameObserver(fit);
      observer.current.observe(body);
    }
  };

  const toggle = <T extends string>(value: T, current: T, set: (next: T) => void, label: string) => (
    <button
      key={value}
      type="button"
      className={`btn btn-sm ${current === value ? 'btn-primary' : 'btn-outline-primary'}`}
      aria-pressed={current === value}
      onClick={() => set(value)}
    >
      {label}
    </button>
  );

  return (
    <div>
      <p className="text-muted">{t('adminNotifications.preview.help')}</p>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-lg-6">
          <label className="form-label" htmlFor="notification-preview-template">
            {t('adminNotifications.preview.template')}
          </label>
          <select
            id="notification-preview-template"
            className="form-select"
            value={eventKey}
            disabled={!templates?.length}
            onChange={event => setEventKey(event.target.value)}
          >
            {(templates ?? []).map(template => (
              <option key={template.eventKey} value={template.eventKey}>
                {t('adminNotifications.preview.option', { eventKey: template.eventKey, category: template.category })}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-sm-6 col-lg-2">
          <label className="form-label" htmlFor="notification-preview-language">
            {t('adminNotifications.preview.language')}
          </label>
          <select
            id="notification-preview-language"
            className="form-select"
            value={language}
            onChange={event => setLanguage(event.target.value as 'sv' | 'en')}
          >
            <option value="sv">{t('adminNotifications.preview.swedish')}</option>
            <option value="en">{t('adminNotifications.preview.english')}</option>
          </select>
        </div>
        <div className="col-12 col-sm-6 col-lg-4 d-flex flex-wrap gap-2 notification-preview-toggles">
          <div className="btn-group" role="group" aria-label={t('adminNotifications.preview.channel')}>
            {toggle<Channel>('html', channel, setChannel, t('adminNotifications.preview.html'))}
            {toggle<Channel>('text', channel, setChannel, t('adminNotifications.preview.text'))}
          </div>
          <div className="btn-group" role="group" aria-label={t('adminNotifications.preview.width')}>
            {toggle<Width>('mobile', width, setWidth, t('adminNotifications.preview.mobile'))}
            {toggle<Width>('desktop', width, setWidth, t('adminNotifications.preview.desktop'))}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{t('adminNotifications.preview.loadError')}</div>}
      {!preview && !error ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('adminNotifications.preview.loading')}</span>
          </div>
        </div>
      ) : preview && (
        <div>
          <dl className="row small mb-3">
            <dt className="col-sm-3 col-lg-2">{t('adminNotifications.preview.subject')}</dt>
            <dd className="col-sm-9 col-lg-10 fw-semibold text-break">{preview.subject}</dd>
          </dl>
          {preview.language !== language && (
            <p className="small text-muted">{t('adminNotifications.preview.onlySwedish')}</p>
          )}
          <div className={`notification-preview-stage notification-preview-stage--${width}`}>
            {channel === 'html' ? (
              <iframe
                ref={frame}
                title={t('adminNotifications.preview.frameTitle', { eventKey: preview.eventKey })}
                className="notification-preview-frame"
                sandbox="allow-same-origin"
                srcDoc={preview.html}
                onLoad={fitFrame}
              />
            ) : (
              <pre className="notification-preview-text">{preview.text}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationTemplatePreview;
